/**
 * useHelp - Hook pour le Help Drawer contextuel.
 *
 * Gère l'état du drawer, le chargement des articles contextuels,
 * et le raccourci clavier (Cmd + /).
 *
 * Migré vers React Query pour le fetching (staleTime: 2 min, lazy load).
 *
 * Ce fichier est un HUB : les types (`useHelp.types.ts`) et les fonctions de
 * fetch/persistance (`useHelp.fetch.ts`) sont extraits en modules frères et
 * ré-exportés ici. L'API publique (`@/hooks/useHelp`) est inchangée.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import * as Sentry from '@sentry/react';
import { toast } from 'sonner';
import { API_BASE, apiFetch } from '@/lib/api';
import { queryKeys } from '@/lib/queryKeys';
import {
    type HelpArticle,
    type HelpDrawerMode,
    type UseHelpOptions,
    type UseHelpReturn,
} from './useHelp.types';
import {
    DESKTOP_MEDIA_QUERY,
    HELP_MODE_STORAGE_KEY,
    fetchHelpArticles,
    readStoredHelpMode,
} from './useHelp.fetch';

// Ré-exports : préserve l'API publique importable depuis `@/hooks/useHelp`.
export type { HelpArticle, HelpDrawerMode, UseHelpOptions, UseHelpReturn };

/**
 * Hook principal pour le Help Drawer.
 *
 * @example
 * function InvoicePage() {
 *   const help = useHelp({ context: 'page:invoice-list' });
 *   // Le drawer affichera les articles taggés 'page:invoice-list'
 * }
 */
export function useHelp(options: UseHelpOptions = {}): UseHelpReturn {
    const { context, contexts, enableKeyboardShortcut = true } = options;
    // Normalise les contextes : supporte context (legacy) ou contexts (nouveau)
    // Clé stable pour éviter les re-renders quand l'appelant passe un tableau non mémorisé
    const contextsKey = contexts?.join('\0') ?? '';
    const normalizedContexts = useMemo(
        () => contexts || (context ? [context] : []),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [contextsKey, context],
    );
    const { t, i18n } = useTranslation();
    const lang = i18n.language?.slice(0, 2) || 'fr';

    // ── UI state (local) ─────────────────────────────────────────────
    const [isOpen, setIsOpen] = useState(false);
    const [currentArticle, setCurrentArticle] = useState<HelpArticle | null>(null);

    const open = useCallback(() => setIsOpen(true), []);
    const close = useCallback(() => setIsOpen(false), []);
    const toggle = useCallback(() => setIsOpen((prev) => !prev), []);

    // Réf. miroir de l'article ouvert : permet aux effets dépendant des articles
    // de savoir si une lecture est en cours SANS se ré-exécuter quand elle change
    // (indispensable au « gel pendant la lecture », cf. plus bas).
    const currentArticleRef = useRef<HelpArticle | null>(null);
    useEffect(() => {
        currentArticleRef.current = currentArticle;
    }, [currentArticle]);

    // ── Mode d'affichage overlay/docked (persisté) ───────────────────
    const [mode, setModeState] = useState<HelpDrawerMode>(readStoredHelpMode);
    const setMode = useCallback((next: HelpDrawerMode) => {
        setModeState(next);
        try {
            window.localStorage.setItem(HELP_MODE_STORAGE_KEY, next);
        } catch {
            /* localStorage indisponible : on garde le mode en mémoire seulement */
        }
    }, []);
    const toggleMode = useCallback(
        () => setMode(mode === 'docked' ? 'overlay' : 'docked'),
        [mode, setMode],
    );

    // ── Détection desktop (≥ lg) : en dessous, on force l'overlay ─────
    const [isDesktop, setIsDesktop] = useState(() =>
        typeof window !== 'undefined' ? window.matchMedia(DESKTOP_MEDIA_QUERY).matches : false,
    );
    useEffect(() => {
        const mql = window.matchMedia(DESKTOP_MEDIA_QUERY);
        const onChange = (e: MediaQueryListEvent) => setIsDesktop(e.matches);
        setIsDesktop(mql.matches);
        mql.addEventListener('change', onChange);
        return () => mql.removeEventListener('change', onChange);
    }, []);

    const isDocked = mode === 'docked' && isOpen && isDesktop;

    const backToList = useCallback(() => {
        setCurrentArticle(null);
    }, []);

    // ── React Query: articles fetch (lazy load when drawer opens) ────
    const {
        data: articles = [],
        isLoading: queryLoading,
        error: queryError,
        isFetching,
    } = useQuery({
        queryKey: queryKeys.help.articles(normalizedContexts, lang),
        queryFn: () => fetchHelpArticles(normalizedContexts, lang),
        enabled: isOpen,
        staleTime: 2 * 60_000,
    });

    // isLoading = true only on first load (no cache), mirrors old behaviour
    const isLoading = queryLoading || isFetching;
    const error = queryError?.message ?? null;

    // ── Auto-show logic: si un seul article, l'afficher directement ──
    // Garde `!currentArticleRef` : en navigation vers une zone à 1 article alors
    // qu'on lit DÉJÀ un article, on n'écrase pas la lecture en cours (gel).
    useEffect(() => {
        if (articles.length === 1 && !currentArticleRef.current) {
            setCurrentArticle(articles[0]);
        }
    }, [articles]);

    // Gel pendant la lecture : on NE réinitialise PAS l'article ouvert quand la
    // ROUTE change. En mode ancré, l'article lu reste donc figé pendant qu'on
    // navigue dans l'app ; seule la LISTE (quand aucun article n'est ouvert)
    // continue de suivre la page. On ne réinitialise que sur changement de LANGUE
    // (l'article était dans l'autre langue).
    useEffect(() => {
        setCurrentArticle(null);
    }, [lang]);

    // À la fermeture du drawer, on oublie l'article lu : la prochaine ouverture
    // repart sur l'aide contextuelle de la page courante (et non sur un article figé).
    useEffect(() => {
        if (!isOpen) setCurrentArticle(null);
    }, [isOpen]);

    // ── showArticle: from cache or single-article fetch ──────────────
    const showArticle = useCallback((slug: string) => {
        const article = articles.find((a) => a.slug === slug);
        if (article) {
            setCurrentArticle(article);
        } else {
            // Charger l'article depuis l'API (fallback hors cache)
            (async () => {
                try {
                    const response = await apiFetch(
                        `${API_BASE}/knowledge/articles/${slug}?lang=${lang}`
                    );
                    if (!response.ok) throw new Error("Article not found");
                    const fetched = await response.json();
                    setCurrentArticle(fetched);
                } catch (err) {
                    // ⚠️ NE JAMAIS REVENIR À UN `catch {}` MUET ICI.
                    //
                    // Ce repli sert les renvois vers un article qui n'est PAS
                    // dans le contexte de la page courante — notamment les
                    // « Comment ça marche ? » des notes de version. Un slug
                    // faux ne produisait alors RIEN : pas d'erreur, pas de
                    // journal, pas de volet. Le bouton avait l'air normal et
                    // ne faisait rien, indéfiniment et sans témoin.
                    //
                    // La première défense reste le gardien
                    // `components/changelog/verifierNotes.ts`, qui refuse un
                    // slug absent de l'un des deux corpus. Ceci est la seconde :
                    // si un renvoi meurt quand même (article renommé six mois
                    // plus tard, retiré), on le voit au lieu de le subir.
                    Sentry.captureException(err, {
                        tags: { feature: 'help-drawer', slug, lang },
                    });
                    toast.error(
                        t('help.article_unavailable', "Cet article d'aide n'est pas disponible."),
                    );
                }
            })();
        }
    }, [articles, lang, t]);

    // ── Raccourci clavier Cmd + / (ou Ctrl + /) ──────────────────────
    useEffect(() => {
        if (!enableKeyboardShortcut) return;

        const handleKeyDown = (event: KeyboardEvent) => {
            // ── Raccourci principal (affiché) : ⌘/ (Mac) · Ctrl+/ (Win/Linux) ──
            // On teste aussi `event.code === 'Slash'` : sur les dispositions non
            // QWERTY (ex. AZERTY FR), `/` exige Maj — combo capté par le système —
            // alors que la touche physique conserve le code `Slash`.
            if ((event.metaKey || event.ctrlKey) && (event.key === "/" || event.code === "Slash")) {
                event.preventDefault();
                toggle();
                return;
            }

            // ── Alias silencieux (non affichés), multiplateformes ──────────────
            const noChord = !event.metaKey && !event.ctrlKey && !event.altKey;
            const target = event.target as HTMLElement | null;
            const isTyping =
                !!target &&
                (target.tagName === "INPUT" ||
                    target.tagName === "TEXTAREA" ||
                    target.isContentEditable);

            // F1 : touche « Aide » universelle (réflexe Windows), non imprimable
            // → active même depuis un champ de saisie.
            if (noChord && event.key === "F1") {
                event.preventDefault();
                toggle();
                return;
            }
            // « ? » : réflexe des apps web ; imprimable → ignoré pendant la saisie.
            if (noChord && !isTyping && event.key === "?") {
                event.preventDefault();
                toggle();
                return;
            }

            // Escape pour fermer
            if (event.key === "Escape" && isOpen) {
                close();
            }
        };

        document.addEventListener("keydown", handleKeyDown);
        return () => document.removeEventListener("keydown", handleKeyDown);
    }, [enableKeyboardShortcut, toggle, close, isOpen]);

    return {
        isOpen,
        open,
        close,
        toggle,
        articles,
        currentArticle,
        showArticle,
        backToList,
        isLoading,
        error,
        mode,
        setMode,
        toggleMode,
        isDesktop,
        isDocked,
    };
}

export default useHelp;
